import { Icon } from '@iconify/react';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import classNames from 'classnames';
import { ComponentPropsWithoutRef, ReactElement, useState } from 'react';

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/Domain/components/Shared/Tooltip/Tooltip';
import { cn } from '@/Domain/utils/cn';
import { copyToClipboard } from '@/Domain/utils/copyToClipboard';

type Props = {
  label: string;
  tooltipContentProps?: ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>;
  tooltipValue?: string;
} & ({ value: string; ValueComponent?: never } | { value?: any; ValueComponent: ReactElement });

export const DetailsItem = ({
  label,
  value,
  ValueComponent,
  tooltipContentProps,
  tooltipValue,
}: Props) => {
  const [copied, setCopied] = useState(false);
  const [showCopyButton, setShowCopyButton] = useState(false);

  const handleCopy = () => {
    copyToClipboard(value?.toString(), () => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1000);
    });
  };

  const hasValue = value || ValueComponent;

  return (
    <section className="grid grid-cols-2 items-center leading-5 tracking-wide gap-x-0">
      <p className="text-sm text-muted-foreground">{label}</p>
      <TooltipProvider>
        <Tooltip open={hasValue && !showCopyButton ? undefined : false}>
          <TooltipTrigger asChild>
            <div
              className="relative w-full overflow-visible"
              onMouseEnter={() => setShowCopyButton(true)}
              onMouseLeave={() => setShowCopyButton(false)}
            >
              {ValueComponent ? (
                ValueComponent
              ) : (
                <p
                  className={classNames('truncate text-sm text-foreground', {
                    'text-muted-foreground': !value,
                  })}
                >
                  {value || '-'}
                </p>
              )}
              {hasValue && (
                <div
                  className={cn(
                    'absolute inset-0 z-10 flex items-center backdrop-blur-[0.5px] transition-opacity duration-200',
                    showCopyButton
                      ? 'opacity-100 pointer-events-auto'
                      : 'opacity-0 pointer-events-none',
                  )}
                >
                  <button
                    type="button"
                    onClick={handleCopy}
                    aria-label={copied ? 'Copied' : 'Copy'}
                    className="flex items-center gap-1 rounded-md border border-border bg-card px-2 py-0.5 text-[10px] text-muted-foreground hover:bg-accent focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                  >
                    <Icon
                      icon={copied ? 'mingcute:check-line' : 'mingcute:copy-line'}
                      className="h-3 w-3"
                    />
                    <span>{copied ? 'Copied' : 'Copy'}</span>
                  </button>
                </div>
              )}
            </div>
          </TooltipTrigger>
          <TooltipContent {...tooltipContentProps}>{tooltipValue ?? value}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </section>
  );
};
